import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import classnames from 'classnames'
import { Button, FeatherLoader } from '@abroad/components'

const ScheduleTimeSlots = ({
  slots = [],
  selectedSlot,
  onSlotSelect,
  initialFormat = '12h',
  isLoading = false,
  timezoneOffsetMinutes = null,
  timezone = null,
  showBackButton = false,
  onBackClick = () => {},
}) => {
  const [timeFormat, setTimeFormat] = useState(initialFormat)

  useEffect(() => {
    setTimeFormat(initialFormat)
  }, [initialFormat])

  const isSelected = (slot) => {
    if (!selectedSlot || !slot) return false
    // Compare by id if available, otherwise compare by start time
    if (slot.id && selectedSlot.id) {
      return slot.id === selectedSlot.id
    }
    // Fallback to comparing start timestamps
    return slot.start === selectedSlot.start
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return ''
    let baseTime
    if (timezone) {
      // When timezone string is provided, use moment-timezone to convert from UTC
      // This automatically handles DST for the specific date
      baseTime = moment.utc(timestamp).tz(timezone)
    } else if (timezoneOffsetMinutes === null) {
      // When no timezone is provided, explicitly convert from UTC to browser's local timezone
      baseTime = moment.utc(timestamp).local()
    } else {
      // When only timezone offset is provided, convert from UTC to the specified timezone offset
      baseTime = moment.utc(timestamp).utcOffset(timezoneOffsetMinutes)
    }
    return timeFormat === '12h'
      ? baseTime.format('h:mm A')
      : baseTime.format('HH:mm')
  }

  const handleSlotClick = (slot) => {
    if (!onSlotSelect) return
    onSlotSelect(slot)
  }

  const hasSlots = Array.isArray(slots) && slots.length > 0

  return (
    <div className='time-slot-picker'>
      <div className='time-slot-picker__header w-100'>
        <div className='d-flex justify-content-between align-items-center w-100'>
          {showBackButton ? (
            <div className='d-flex align-items-center'>
              <Button
                variant='none'
                className='p-0 border-0 box-shadow-none text-saffron-700 hover cursor-pointer mr-2'
                onClick={onBackClick}>
                <svg
                  width='11'
                  height='19'
                  viewBox='0 0 11 19'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M9.49615 1.25L1.25 9.25003'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                  />
                  <path
                    d='M9.49615 17.25L1.25 9.25'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                  />
                </svg>
              </Button>
              <span
                className='text-saffron-700 hover cursor-pointer sa5'
                onClick={onBackClick}>
                Back to all dates
              </span>
            </div>
          ) : (
            <span className='sa4 font-16px font-weight-bold'>Time slots</span>
          )}
          <div className='time-slot-picker__format-toggle'>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn', {
                'time-slot-picker__format-btn--active': timeFormat === '12h',
              })}
              onClick={() => setTimeFormat('12h')}>
              12h
            </button>
            <button
              type='button'
              className={classnames('time-slot-picker__format-btn', {
                'time-slot-picker__format-btn--active': timeFormat === '24h',
              })}
              onClick={() => setTimeFormat('24h')}>
              24h
            </button>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className='time-slot-picker__empty mt-20px d-flex justify-content-center align-items-center'>
          <FeatherLoader />
        </div>
      ) : !hasSlots ? (
        <div className='time-slot-picker__empty mt-20px'>
          No available time slots for this date
        </div>
      ) : (
        <div className='time-slot-picker__slots mt-20px'>
          {slots.map((slot, index) => {
            const selected = isSelected(slot)
            const label = formatTime(slot.start)

            return (
              <Button
                key={slot?.id || slot?.start || index}
                variant='abroad-blue'
                size='lg'
                className={classnames(
                  'py-2 mb-1 time-slot-picker__slot text-center',
                  {
                    'time-slot-picker__slot--selected': selected,
                  },
                )}
                onClick={() => handleSlotClick(slot)}>
                {label}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}

ScheduleTimeSlots.propTypes = {
  slots: PropTypes.array,
  selectedSlot: PropTypes.object,
  onSlotSelect: PropTypes.func,
  initialFormat: PropTypes.string,
  isLoading: PropTypes.bool,
  timezoneOffsetMinutes: PropTypes.number,
  timezone: PropTypes.string,
  showBackButton: PropTypes.bool,
  onBackClick: PropTypes.func,
}

export default ScheduleTimeSlots
